<script setup lang="ts">
import type { InteractionPreviewProps } from '../../../../model/assessments/preview-interactions.hook'

import { AppText } from '@shared/ui/typography'

import { usePreviewDragTheWords } from '../../../../model/assessments/preview-interactions.hook'
import PreviewAuthorNote from '../PreviewAuthorNote.vue'

/**
 * The sentence with each `*word*` rendered as a gap, plus the bank a kid would drag from: the gap
 * answers and the distractors, annotated as such. Authored order, deliberately NOT shuffled — a
 * preview must be deterministic. Split with the schema's `DRAG_GAP_REGEX`; rendered as text.
 */
defineOptions({
  name: 'PreviewDragTheWords',
})

const props = defineProps<InteractionPreviewProps>()

const {
  refSegments,
  refGapCount,
  refHasText,
  refBank,
} = usePreviewDragTheWords(() => props.interaction)
</script>

<template>
  <div class="preview-drag-the-words">
    <AppText
      v-if="!refHasText"
      name="body2"
      color="slate-500"
    >
      No text yet — a kid would see an empty sentence.
    </AppText>

    <p
      v-else
      class="preview-drag-the-words__text"
    >
      <template
        v-for="segment in refSegments"
        :key="segment.key"
      >
        <span v-if="segment.kind === 'text'">{{ segment.value }}</span>
        <span
          v-else
          class="preview-drag-the-words__gap"
          role="img"
          :title="`Gap ${segment.position}`"
          :aria-label="`Gap ${segment.position}`"
        />
      </template>
    </p>

    <!-- Refinement 3's case: text with no `*word*` marker. Stated, not judged. -->
    <AppText
      v-if="refHasText && refGapCount === 0"
      name="body2"
      color="slate-500"
    >
      This text has no marked words, so there is nothing for a kid to drag into.
    </AppText>

    <AppText
      name="body2"
      color="slate-500"
    >
      Word bank
    </AppText>

    <AppText
      v-if="refBank.length === 0"
      name="body2"
      color="slate-500"
    >
      The bank is empty.
    </AppText>

    <ul
      v-else
      class="preview-drag-the-words__bank"
    >
      <li
        v-for="item in refBank"
        :key="item.key"
        class="preview-drag-the-words__word"
      >
        {{ item.word || '(blank)' }}
        <PreviewAuthorNote v-if="item.isDistractor">
          distractor — belongs in no gap
        </PreviewAuthorNote>
      </li>
    </ul>

    <PreviewAuthorNote>
      the bank is shown in authored order, so the answers line up with the gaps. A kid's bank is
      shuffled.
    </PreviewAuthorNote>
  </div>
</template>

<style scoped lang="scss" src="./PreviewDragTheWords.scss" />
